import {
  canonicalInternalPath,
  INTERNAL_APP_ORIGIN,
  normalizeInternalAppReturnPath,
} from '@/types/internalNavigation';
import {
  getCatalogRouteFromPath,
  getRoutePropertyId,
  type FmRouteCatalogEntry,
} from '@/shared/ui/FmRouteCatalog';
import { fmRouteCatalogDefault } from '@/shared/ui/FmRouteCatalogDefault';
import { appRoutePatterns } from '@/shared/navigation/appRoutePatterns';
import { decodePlanKey } from '@/utils/planKey';

type ImplementReturnRoute = Extract<
  (typeof fmRouteCatalogDefault.routes)[number],
  { implementReturn: true }
>;

export type ImplementReturnOrigin = ImplementReturnRoute['id'];

export interface ImplementReturnTarget {
  origin: ImplementReturnOrigin;
  path: string;
}

interface ImplementHandoffInput {
  propertyId: string;
  returnTo: string;
}

function propertyPath(pattern: string, propertyId: string): string {
  return pattern.replace(':propertyId', encodeURIComponent(propertyId));
}

function overviewPath(propertyId: string): string {
  return propertyPath(appRoutePatterns.forestOverview, propertyId);
}

function isImplementReturnRoute(
  route: FmRouteCatalogEntry,
): route is FmRouteCatalogEntry & ImplementReturnRoute {
  return route.implementReturn === true;
}

function matchesQueryPropertyContext(
  route: FmRouteCatalogEntry,
  url: URL,
  propertyId: string,
): boolean {
  const context = route.propertyContext;
  if (!context) return true;
  const values = url.searchParams.getAll(context.parameter);
  if (values.length === 0) {
    return context.requirement === 'optional'
      && !context.requiredWithParameters?.some((parameter) => (
        url.searchParams.has(parameter)
      ));
  }
  return values.length === 1
    && values[0]?.trim() === propertyId;
}

function resolvedOwnerReturn(
  returnTo: string | null,
  propertyId: string,
): ImplementReturnTarget | null {
  let safeReturn = normalizeInternalAppReturnPath(returnTo);
  for (let depth = 0; safeReturn && depth < 2; depth += 1) {
    const url = new URL(safeReturn, INTERNAL_APP_ORIGIN);
    const route = getCatalogRouteFromPath(
      fmRouteCatalogDefault,
      url.pathname,
    );
    if (!route) return null;
    if (route.id === 'implement') {
      safeReturn = normalizeInternalAppReturnPath(
        url.searchParams.get('return_to'),
      );
      continue;
    }
    if (!isImplementReturnRoute(route)) return null;
    if (
      route.requiredContext === 'property'
      && getRoutePropertyId(
        route,
        url.pathname,
        url.search,
      ) !== propertyId
    ) {
      return null;
    }
    if (!matchesQueryPropertyContext(route, url, propertyId)) return null;
    return {
      origin: route.id,
      path: safeReturn,
    };
  }
  return null;
}

/**
 * Enter the property-scoped implementation workspace with one validated owner
 * origin. Unsupported paths deliberately lose their return promise instead of
 * becoming an open redirect or a misleading back action.
 */
export function buildImplementHandoff({
  propertyId,
  returnTo,
}: ImplementHandoffInput): string {
  const target = new URL(
    propertyPath(appRoutePatterns.implement, propertyId),
    INTERNAL_APP_ORIGIN,
  );
  const ownerReturn = resolvedOwnerReturn(returnTo, propertyId);
  if (!ownerReturn) return target.pathname;
  target.searchParams.set('return_to', ownerReturn.path);
  return canonicalInternalPath(target);
}

/** Resolve the explicit Implement back action without relying on browser history. */
export function resolveImplementReturnTarget(
  returnTo: string | null,
  propertyId: string,
): ImplementReturnTarget {
  const fallback: ImplementReturnTarget = {
    origin: 'forest-overview',
    path: overviewPath(propertyId),
  };
  return resolvedOwnerReturn(returnTo, propertyId) ?? fallback;
}

/**
 * The saved plan the planner had open when the owner left it for Implement
 * (the Puukauppa menu keeps the planner URL as `return_to`). Returns the plan
 * key's id only; the caller matches it against real saved plans, so an
 * unsaved future's key never resolves to anything.
 */
export function plannerPlanIdFromReturn(
  returnTo: string | null,
  propertyId: string,
): string | null {
  const target = resolvedOwnerReturn(returnTo, propertyId);
  if (target?.origin !== 'operations-planning') return null;
  const planKey = new URL(target.path, INTERNAL_APP_ORIGIN).searchParams.get('plan_id');
  return planKey ? decodePlanKey(planKey).id : null;
}

/** The owner's tender inputs exactly as typed on Implement or the review page. */

